import { Link } from 'react-router-dom';
import { MapPin, Phone, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ServiceAreas() {
  const mainAreas = [
    { city: 'Minneapolis', county: 'Hennepin County', desc: 'Our headquarters and primary service area' },
    { city: 'St. Paul', county: 'Ramsey County', desc: 'Full service coverage' },
    { city: 'Bloomington', county: 'Hennepin County', desc: 'Same-day service available' },
    { city: 'Brooklyn Park', county: 'Hennepin County', desc: 'Full service coverage' },
    { city: 'Plymouth', county: 'Hennepin County', desc: 'Same-day service available' },
    { city: 'Maple Grove', county: 'Hennepin County', desc: 'Full service coverage' },
    { city: 'Woodbury', county: 'Washington County', desc: 'Full service coverage' },
    { city: 'Eden Prairie', county: 'Hennepin County', desc: 'Same-day service available' }
  ];

  const additionalAreas = [
    'Minnetonka', 'Edina', 'Burnsville', 'Lakeville', 'Coon Rapids', 
    'Eagan', 'Apple Valley', 'Blaine', 'Richfield', 'Roseville',
    'Maplewood', 'Shakopee', 'Prior Lake', 'Savage', 'Chanhassen',
    'Hopkins', 'Golden Valley', 'St. Louis Park', 'New Hope', 'Crystal',
    'Robbinsdale', 'Columbia Heights', 'Fridley', 'White Bear Lake', 'Stillwater'
  ];

  const counties = [
    'Hennepin County', 'Ramsey County', 'Dakota County', 'Anoka County', 
    'Washington County', 'Scott County', 'Carver County', 'Wright County'
  ];

  return (
    <div className="page-transition pt-20" data-testid="service-areas-page">
      {/* Hero Section */}
      <section className="section-padding bg-[#F7F9F7]" data-testid="areas-hero">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2E7D32] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Service Areas
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1C1A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Areas We Serve
            </h1>
            <p className="text-lg text-[#52525B]">
              Primes Pick proudly serves Minneapolis and the greater Twin Cities metropolitan area. 
              Check below to see if we service your location.
            </p>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12" data-testid="areas-map">
        <div className="container-main">
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d361553.5179017937!2d-93.6030!3d44.9537!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x52b333909377bbbd%3A0x939fc9842f7aee07!2sMinneapolis%2C%20MN!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Service Areas Map"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Main Service Areas */}
      <section className="section-padding" data-testid="main-areas">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Primary Service Areas
            </h2>
            <p className="text-lg text-[#52525B] max-w-2xl mx-auto">
              We provide full pest control services with same-day availability in these cities
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {mainAreas.map((area, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-6 shadow-lg border border-border hover:border-[#2E7D32]/30 transition-colors"
                data-testid={`main-area-${index}`}
              >
                <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-[#2E7D32]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1C1A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {area.city}
                </h3>
                <p className="text-sm text-[#2E7D32] font-medium mb-2">{area.county}</p>
                <p className="text-sm text-[#52525B]">{area.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Areas */}
      <section className="section-padding bg-[#F7F9F7]" data-testid="additional-areas">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Additional Cities We Serve
            </h2>
            <p className="text-lg text-[#52525B] max-w-2xl mx-auto">
              We also provide service to these surrounding communities
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {additionalAreas.map((city, index) => (
              <div 
                key={index} 
                className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-border"
              >
                <CheckCircle className="w-4 h-4 text-[#2E7D32]" />
                <span className="text-[#1A1C1A] font-medium">{city}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Counties */}
      <section className="section-padding" data-testid="counties-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Counties We Cover
            </h2>
            <p className="text-lg text-[#52525B] max-w-2xl mx-auto">
              Our service extends across the entire Twin Cities metro area
            </p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {counties.map((county, index) => (
              <div 
                key={index} 
                className="bg-[#E8F5E9] rounded-xl p-4 text-center"
              >
                <p className="font-semibold text-[#2E7D32]">{county}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Not Listed */}
      <section className="section-padding bg-[#E8F5E9]" data-testid="not-listed">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Don't See Your City Listed?
            </h2>
            <p className="text-lg text-[#52525B] mb-8">
              We may still be able to help! Contact us to check if we service your area. 
              We are constantly expanding our coverage to serve more communities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="btn-primary w-full sm:w-auto" data-testid="areas-contact-btn">
                  Contact Us
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+18883568891" className="btn-accent w-full sm:w-auto text-center">
                <Phone className="w-5 h-5" />
                Call: +1-888-356-8891
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-16 sm:py-20" data-testid="areas-cta">
        <div className="container-main text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready for Pest-Free Living?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Schedule your free inspection today and take the first step towards a safer, healthier home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-[#2E7D32] hover:bg-white/90 rounded-full px-8 py-4 font-semibold shadow-lg">
                Schedule Free Inspection
              </Button>
            </Link>
            <a href="tel:+18883568891" className="btn-accent">
              <Phone className="w-5 h-5" />
              Call: +1-888-356-8891
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
